import { Global } from "../Global";
import { BuyerInfoDTO } from "../models/Buyer";
import { post } from "../utils/ApiUtil";    
import { Storage } from "../utils/Storage";

// 获取用户信息
const getBuyerByToken = async (): Promise<BuyerInfoDTO | null> => {
    if(Global.isMockMode()){ 
        const token: string | null = await Storage.getItem('token');
        if(token==='mocked-token-456'){
            return Global.getMockData("mocked-token-456");
        }
        if(token==='mocked-token-123'){
            return Global.getMockData("mocked-token-123");
        }
        const res = Global.getMockData("mocked-token-123");
        console.log("getBuyerByToken 当前用户信息：", res);
        return res;
    }
    const resultOrErrorCode  =  await post<null, CurrentUser>("/user/getUserInfo", null);
    if(typeof resultOrErrorCode === 'string'){
        // 错误码处理
        return null;
    }
    const currentUser = resultOrErrorCode;
    if(!currentUser){
        return null;
    }
    return convertToBuyerInfo(currentUser); 
};

interface CurrentUser {
    token: string;
    userProps: Record<string, any>;  // Map<String, Object> 转为 Record<string, any>
    loginUrl: string;
}

// 转换函数
function convertToBuyerInfo(currentUser: CurrentUser): BuyerInfoDTO {
    // 检查 userProps 是否存在，如果不存在则使用 buyerProps（兼容不同 API 格式）
    const props = currentUser.userProps || (currentUser as any).buyerProps;
    if (!props) {
        throw new Error('用户数据格式错误：缺少 userProps 或 buyerProps');
    }
    
    // 初始化 UserInfo 对象
    const userInfo: BuyerInfoDTO = {
        buyerId: props.id,
        trueName: props.name,
        nickname: props.nickname,
        gender: props.gender,
        avatarUrl: props.avatarUrl,
        phone: props.phone,
        balance: props.balance,
        isCompany: props.isCompany,
        company: props.isCompany ? props.companyName : undefined,
        level: props.level || 1,
        levelDesc: props.levelDesc || '青铜'
    };

    return userInfo;
}

// 用户登录/注册
const loginByPhoneAndVerifyCode = async (phoneNo: string, verificationNo: string, extraInfo?: Record<string, any>): Promise<BuyerInfoDTO | null> => {
    if(Global.isMockMode()){ 
        console.log("__________mock登录，用户手机号：", phoneNo);
        const userKey = phoneNo === '+8618217023008' ? 'mocked-token-123' : 'mocked-token-456';
        const loginedUser = Global.getMockData(userKey);
        await Storage.setItem("token", loginedUser.token);
        console.log("__________登录成功，用户TOKEN：", loginedUser.token);
        return loginedUser;
    }
    // 接口可能需要同时传递 password 和 verifyCode，根据哪个有值来判断登录方式
    const resultOrErrorCode = await post<{ 
            phoneNo: string; 
            code: string;
            extraInfo: Record<string, any> 
        }, CurrentUser>("/user/login", { 
        phoneNo: phoneNo || '', 
        code: verificationNo || '', 
        extraInfo: extraInfo || {} 
    });
    if(typeof resultOrErrorCode === 'string'){
        // 错误码处理，解析错误码和错误信息
        const [errorCode, errorMessage] = resultOrErrorCode.split('|');
        const error = new Error(errorMessage || errorCode);
        (error as any).errorCode = errorCode;
        throw error;
    }
    const currentUser = resultOrErrorCode;
    if(!currentUser.token){
        throw new Error('登录失败：未获取到token');
    }
    await Storage.setItem("token", currentUser.token);
    console.log("__________登录成功，用户TOKEN：", currentUser.token);
    return convertToBuyerInfo(currentUser); 
}; 

const loginByPassword = async (phoneNo: string, password: string, extraInfo?: Record<string, any>): Promise<BuyerInfoDTO | null> => {
    if(Global.isMockMode()){
        const userKey = phoneNo === '18217023008' ? 'mocked-token-123' : 'mocked-token-456';
        const loginedUser = Global.getMockData(userKey);
        await Storage.setItem("token", loginedUser.token);
        console.log("__________登录成功，用户TOKEN：", loginedUser.token);
        return loginedUser;
    }
    // 接口可能需要同时传递 password 和 verifyCode，根据哪个有值来判断登录方式
    const resultOrErrorCode = await post<{ phoneNo: string; password: string; extraInfo: Record<string, any> }, CurrentUser>("/user/login", { 
        phoneNo, 
        password, 
        extraInfo: extraInfo || {} 
    });
    if(typeof resultOrErrorCode === 'string'){
        // 错误码处理，解析错误码和错误信息
        const [errorCode, errorMessage] = resultOrErrorCode.split('|');
        if(errorCode === '0000007'){
            throw new Error('NOUSER');
        }
        const error = new Error(errorMessage || errorCode);
        (error as any).errorCode = errorCode;
        throw error;
    }
    const userInfo = resultOrErrorCode;
    if(!userInfo.token){
        throw new Error('登录失败：未获取到token');
    }
    await Storage.setItem("token", userInfo.token);
    return convertToBuyerInfo(userInfo); 
};

// 用户登出
const logout = async (): Promise<boolean> => {
    if(Global.isMockMode()){
        return true;
    }
    const token = await Storage.getItem("token");
    console.log("__________登出，用户TOKEN：", token);
    const resultOrErrorCode = await post<{}, boolean>("/user/logout", {});
    if(typeof resultOrErrorCode === 'string'){
        // 错误码处理
        return false;
    }
    return resultOrErrorCode;
};

const getPhoneAreaCode = () => {
    if(Global.isMockMode()){
        return Global.getMockData("getCountryCode");
    }
}

// 获取用户公开信息
const getBuyerInfo = async (buyerIds: string[]): Promise<BuyerInfoDTO[]> => {
    if(Global.isMockMode()){
        return Global.getMockData("getBuyerInfo");
    }
    const resultOrErrorCode = await post<string[], BuyerInfoDTO[]>("/buyer/getBuyerInfo", buyerIds);
    if(typeof resultOrErrorCode === 'string'){
        // 错误码处理
        return [];
    }
    return resultOrErrorCode;
};


// 更新用户信息
const updateBuyerInfo = async (userInfo: Partial<BuyerInfoDTO>): Promise<boolean> => {
    if(Global.isMockMode()){
        return true;
    }
    const resultOrErrorCode = await post<Partial<BuyerInfoDTO>, boolean>("/buyer/updateBuyerInfo", userInfo);
    if(typeof resultOrErrorCode === 'string'){
        // 错误码处理
        return false;
    }
    return resultOrErrorCode;
};

// 提交软件开发需求
const postSoftwareDevDemand = async (demandDescript: string): Promise<boolean> => {
    if(Global.isMockMode()){
        console.log("提交软件开发需求：", demandDescript);
        return true;
    }
    const resultOrErrorCode = await post<{ demandDescript: string }, boolean>("/buyer/postSoftwareDevDemand", { demandDescript });
    if(typeof resultOrErrorCode === 'string'){
        // 错误码处理
        return false;
    }
    return resultOrErrorCode;
};
 
export const BuyerApi = {
    getBuyerByToken,
    loginByPhoneAndVerifyCode,
    loginByPassword, 
    logout,
    getPhoneAreaCode, 
    getBuyerInfo,
    updateBuyerInfo,
    postSoftwareDevDemand,
};
